/**
 * Excel (.xlsx) export helpers.
 *
 * `write-excel-file` is imported lazily so its bundle only loads when someone actually
 * exports something — it must stay out of the main chunk.
 */

/** A column in the exported sheet. `value` is read per row. */
export interface XlsxColumn<T> {
  header: string;
  /** Rendered as text; return "" for a blank cell. */
  value: (row: T) => string;
  /** Column width in characters. Defaults to a width derived from the content. */
  width?: number;
}

const MIN_COLUMN_WIDTH = 12;
const MAX_COLUMN_WIDTH = 48;

/** Widens a column to fit its content, within sane bounds, so nothing is cut off. */
const columnWidth = <T>(column: XlsxColumn<T>, rows: T[]): number => {
  if (column.width) return column.width;

  const longestValue = rows.reduce(
    (longest, row) => Math.max(longest, column.value(row).length),
    column.header.length
  );

  return Math.min(
    Math.max(longestValue + 2, MIN_COLUMN_WIDTH),
    MAX_COLUMN_WIDTH
  );
};

/**
 * Builds a real .xlsx workbook from `rows`. The header row is bold and stays visible
 * while scrolling.
 */
export const buildXlsxBlob = async <T>(
  sheetName: string,
  columns: XlsxColumn<T>[],
  rows: T[]
): Promise<Blob> => {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");

  return writeXlsxFile(rows, {
    columns: columns.map((column) => ({
      header: { value: column.header, fontWeight: "bold" as const },
      width: columnWidth(column, rows),
      cell: (row: T) => ({
        value: column.value(row) || undefined,
        type: String,
      }),
    })),
    sheet: sheetName,
    stickyRowsCount: 1,
  }).toBlob();
};

/** Builds the workbook and hands it to the browser as a download. */
export const downloadXlsx = async <T>(
  filename: string,
  sheetName: string,
  columns: XlsxColumn<T>[],
  rows: T[]
): Promise<void> => {
  const blob = await buildXlsxBlob(sheetName, columns, rows);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

/** Normalises a value for use inside a download filename. */
const toFilenamePart = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9æøå-]/g, "")
    // Dropped characters can leave runs of separators behind ("A/S Ærø & Co").
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);

/** e.g. `brugere_3fa_2026-08-21.xlsx` */
export const buildExportFilename = (
  prefix: string,
  tenantName: string | undefined,
  today: Date
): string => {
  const parts = [prefix];
  if (tenantName) parts.push(toFilenamePart(tenantName));
  parts.push(today.toISOString().slice(0, 10));

  return `${parts.join("_")}.xlsx`;
};
